import React from 'react';
import styled from 'styled-components';

import { ApiContext } from 'gateways/Api';

import AddressBase from './AddressBase';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Select';
import SameLine from 'components/widget/SameLine';

const PostcodeWrapper = styled.div`
  display: flex;

  > .button {
    flex-grow: 0;
    margin-left: 25px;
  }

  @media (max-width: 576px) {
    flex-direction: column;

    > .button {
      margin: 20px 0;
      align-self: stretch;
    }
  }

  > * {
    flex-grow: 1;
  }
`;

const AddressSelectWrapper = styled.div`
  margin: 40px 0;

  @media (max-width: 576px) {
    margin-top: 25px;
  }
`;

export default class UKAddress extends AddressBase {
  static contextType = ApiContext;

  state = {
    houseNo: null,
    houseName: null,
    address: null,
    town: null,
    county: null,
    postcode: null,

    processing: false,
    addressChoices: null
  };

  formValid = () =>
      (!!this.state.houseNo || !!this.state.houseName) &&
        !!this.state.address && !!this.state.town && !!this.state.postcode;

  #findAddress = async () => {
    this.setState({ processing: true });

    try {
      let result = await this.context.addressLookup(this.state.postcode, 'UK');

      // The returned addresses sometimes contain duplicates.  These must be removed, for a better user experience, and
      // also because React can't tell them apart.

      let addressChoices = result.data.message;
      let addressSet = new Set(addressChoices.map(this.#displayAddress));
      addressChoices = addressChoices.filter(x => {
        let display = this.#displayAddress(x);
        let result = addressSet.has(display);
        addressSet.delete(display);
        return result;
      });

      let displayAddresses = addressChoices.map(this.#displayAddress);
      displayAddresses.unshift('— No address selected —');

      this.setState({ addressChoices, displayAddresses });
    } finally {
      this.setState({ processing: false });
    }
  }

  #displayAddress = (ad) => {
    let addressLines = [
      ad.houseName,
      `${ad.houseNumber} ${ad.addressLine1}`,
      ad.townCity,
      ad.district,
      ad.county,
      ad.postCode
    ];

    addressLines = addressLines.map(x => x.trim()).filter(x => x !== '');
    return addressLines.join(', ');
  }

  #selectAddress = (e) => {
    this.handleChange(e);

    let selected = e.target.selectedIndex - 1;
    if (selected < 0)
      return;

    let ad = this.state.addressChoices[selected];
    let changes = {
      houseNo: ad.houseNumber.trim(),
      houseName: ad.houseName,
      address: ad.addressLine1,
      town: ad.townCity,
      county: ad.county,
      postcode: ad.postCode
    };

    this.setState(changes);
    this.props.onChange(changes);
  }

  render() {
    return (
      <div>
        <PostcodeWrapper>
          <Input
            name="postcode"
            label="Postal code"
            placeholder="Required"
            value={this.state.postcode || ''}
            onChange={this.handleChange}
            error={this.props.errors && this.props.errors.postcode}
          />

          <Button
            className="button"
            disabled={!this.state.postcode || this.state.postcode.replace(/ /g, '').length < 4 || this.state.processing}
            onClick={this.#findAddress}>

            {this.state.processing ? 'Please wait...' : 'Find Address'}
          </Button>
        </PostcodeWrapper>

        {this.state.addressChoices &&
          <AddressSelectWrapper>
            <Select
              name="chooseAddress"
              label="Select address"
              options={this.state.displayAddresses}
              onChange={this.#selectAddress}
              value={this.state.chosenAddress}
            />
          </AddressSelectWrapper>
        }

        <SameLine>
          <Input
            name="houseNo"
            label="House no"
            className="smaller"
            value={this.state.houseNo || ''}
            onChange={this.handleChange}
          />

          <Input
            name="houseName"
            label="House name"
            placeholder={(this.state.houseNo) ? 'Optional' : 'Required'}
            value={this.state.houseName || ''}
            onChange={this.handleChange}
          />
        </SameLine>

        <Input
          name="address"
          label="Address"
          placeholder="Required"
          value={this.state.address || ''}
          onChange={this.handleChange}
        />

        <Input
          name="town"
          label="Town/city"
          placeholder="Required"
          value={this.state.town || ''}
          onChange={this.handleChange}
        />

        <Input
          name="county"
          label="County"
          placeholder="Optional"
          value={this.state.county || ''}
          onChange={this.handleChange}
        />
      </div>
    );
  }
}
